/*
 * bc-cart-length
 *
 * Cart-side editors for "by the meter" lines. Custom-length cart lines are
 * tagged in cart-products.liquid with `data-bc-length-per-piece="<m>"` and
 * `data-bc-pieces="<N>"` on the <tr>. The standard cart quantity selector
 * shows the cumulative cart quantity (in meters) — matches what Shopify
 * checkout displays — but is disabled so the customer can't desync it.
 *
 * Customers edit two values instead:
 *   - Pieces (number of cuts)
 *   - Length per piece (length of each cut, in the unit the line was added with)
 *
 * Each editor POSTs to /cart/change.js with the recomputed cart quantity
 * (pieces × length) plus both line item properties (so the cart and
 * checkout stay in sync), then dispatches `cart:update` so Horizon's
 * cart-items-component morphs the section. Segment Builder lines also get
 * their Build permalink's q=/len= params rewritten in the same POST, so
 * the "Edit segment" link reopens the builder on the edited values — the
 * LENGTH param under the prefix of the line's own "Segment Piece" role
 * (len/blen/xlen), since every line of a multi-line build carries the same
 * whole-build permalink; see submitCartChange. The property NAMES are not
 * hardcoded here: cart-products.liquid stamps the names it matched on the
 * row (`data-bc-length-prop` / `data-bc-pieces-prop`) — the global
 * "BC Custom length" theme settings for new lines, or the original
 * legacy names for lines added before those settings existed.
 *
 * An edit that would oversell is REFUSED rather than posted: the recomputed
 * quantity is length units, so a longer cut can ask for more metres than
 * exist, and Shopify answers that by silently clamping the line (see
 * stockRefusal). The customer's numbers are put back and an inline message
 * explains — we never quietly shrink a length they asked for.
 */
(() => {
  if (window.__bcCartLengthInstalled) return;
  window.__bcCartLengthInstalled = true;

  /*
   * Single source of truth for the cart quantity of a custom-length line.
   * Mirrors the PDP add path (bc-custom-length.js) and the price display
   * (bc-price-total.js): the fractional total (length × pieces) is rounded
   * UP so a fractional remainder never undercharges the merchant — e.g.
   * length=24.1, pieces=3 -> 72.3 -> 73. Must stay ceil here; round() would
   * undercharge by up to one unit on any cart-side edit of a fractional line.
   */
  function cartLineQuantity(length, pieces) {
    return Math.max(1, Math.ceil(length * pieces));
  }

  // The standard cart quantity selector is greyed-out via CSS for
  // custom-length lines, but keyboard activation (Tab+Enter on the +/-
  // buttons) could still bypass the visual gate. Capture quantity-selector
  // updates from those rows and abort: stop the event from reaching
  // cart-items-component and restore the input's displayed value.
  document.addEventListener(
    'quantity-selector:update',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const row = target.closest('[data-bc-length-per-piece]');
      if (!row) return;
      event.stopImmediatePropagation();
      const input = target.querySelector?.('input[name="updates[]"]');
      if (input) {
        const length = parseFloat(row.getAttribute('data-bc-length-per-piece'));
        const pieces = parseInt(row.getAttribute('data-bc-pieces'), 10);
        if (Number.isFinite(length) && Number.isFinite(pieces)) {
          // ceil, not round: must match the cart quantity the PDP/checkout
          // actually hold (see cartLineQuantity) so this restored display
          // value never disagrees with the real line.
          input.value = String(cartLineQuantity(length, pieces));
        }
      }
    },
    { capture: true }
  );

  function findRow(el) {
    return el?.closest?.('[data-bc-length-per-piece]') || null;
  }

  function readRowState(row) {
    if (!row) return null;
    const length = parseFloat(row.getAttribute('data-bc-length-per-piece'));
    const pieces = parseInt(row.getAttribute('data-bc-pieces'), 10);
    if (!Number.isFinite(length) || length <= 0) return null;
    return {
      length,
      pieces: Number.isFinite(pieces) && pieces > 0 ? pieces : 1,
    };
  }

  function readLineIndex(el) {
    const n = parseInt(el?.dataset?.line, 10);
    return Number.isFinite(n) ? n : null;
  }

  function readUnit(row) {
    const editor = row?.querySelector?.('bc-cart-length-editor');
    return editor?.dataset?.unit || 'm';
  }

  // The line's stock ceiling, or null when it has none. Stamped on the row
  // by cart-products.liquid and ONLY for variants Shopify actually blocks
  // (inventory tracked + deny overselling), so a missing attribute means
  // "unlimited" — never zero. An unparseable value reads as no limit: no
  // gate beats a wrong one, and Shopify's own clamp still backstops it.
  function readMaxQty(row) {
    const raw = row?.getAttribute?.('data-bc-max-qty');
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  }

  // A cart line has no server-rendered error slot, so the refusal message is
  // injected into the line's variants column — the one container BOTH markup
  // paths render inside (the flat property list in cart-products.liquid and
  // the Segment Builder build sheet in bc-cart-segment-props.liquid), so it
  // lands under the editors either way. Nothing has to clear it on success:
  // it only ever shows on a change we did NOT post, and the next change that
  // does go through re-renders the section over it.
  function setLineError(row, message) {
    if (!row) return;
    const host = row.querySelector('.cart-items__variants-wrapper') || row;
    let el = host.querySelector('.bc-cart-length-error');
    if (!message) {
      el?.remove();
      return;
    }
    if (!el) {
      el = document.createElement('p');
      el.className = 'bc-cart-length-error';
      // Announced, not focus-stealing: the customer is mid-blur and their
      // input has already been put back to its previous value.
      el.setAttribute('role', 'status');
      host.appendChild(el);
    }
    el.textContent = message;
  }

  /*
   * Stock gate, run BEFORE the /cart/change.js POST.
   *
   * Cart quantity on these lines is LENGTH units (1 unit = 1 m or 1 ft), so
   * a length or pieces edit can ask for more metres than exist. Shopify does
   * not reject that on an inventory-tracked, deny-overselling variant: it
   * accepts the change and silently CLAMPS the line to what's in stock,
   * leaving it holding fewer units than its own Piece Length / Qty
   * properties claim — an under-cut order neither the customer nor the
   * cutter is told about. So refuse the change and leave the customer's
   * numbers as they were; we never auto-shrink a length behind their back.
   *
   * Returns the message to show, or null to proceed. `cart` is the /cart.js
   * read this flow already does (see readCartLine) — the freshest view
   * available. The LIMIT comes off the row, which Shopify re-renders after
   * every cart change, so it can only go stale within a single edit (a
   * concurrent order elsewhere draining stock mid-blur); Shopify's clamp
   * remains the backstop for that race.
   */
  function stockRefusal({ row, cart, line, quantity, unit }) {
    const limit = readMaxQty(row);
    if (limit == null) return null;

    // Every line carrying this variant draws on the same stock, whatever its
    // properties, so the ceiling applies to their SUM. If the cart can't be
    // read here the other-lines half fails open (otherQty = 0) rather than
    // blocking a legitimate edit on missing data — that reduces this to the
    // cart-blind `quantity <= limit` check, which still holds the common
    // single-line case. (/cart.js line `id` IS the variant id.)
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const variantId = items[line - 1]?.id;
    const otherQty = variantId == null
      ? 0
      : items.reduce(
          (sum, it, i) => (i !== line - 1 && it.id === variantId ? sum + it.quantity : sum),
          0
        );

    if (quantity + otherQty <= limit) return null;

    // "40 m", or a bare "40" if the line somehow carries no unit label.
    const amount = (n) => (unit ? `${n} ${unit}` : String(n));
    if (otherQty <= 0) return `Only ${amount(limit)} in stock.`;
    return `Only ${amount(Math.max(0, limit - otherQty))} left — other lines in your cart use ${amount(otherQty)}.`;
  }

  // Property names stamped on the row by cart-products.liquid — see the
  // file header. Fallbacks only matter if the row markup predates the
  // attributes (stale section HTML mid-deploy).
  function readPropNames(row) {
    return {
      lengthProp: row?.getAttribute?.('data-bc-length-prop') || 'Piece Length',
      piecesProp: row?.getAttribute?.('data-bc-pieces-prop') || 'Qty',
    };
  }

  // /cart/change.js REPLACES a line's properties wholesale with whatever is
  // passed. Re-read the line's current properties first and spread them
  // under ours, so sibling properties (e.g. bc-end-treatment's End 1 / End 2)
  // survive a cart-side length or pieces edit. Returns null when the line's
  // current properties can't be established (failed /cart.js fetch, or the
  // line index no longer exists) — the caller MUST abort the change then:
  // proceeding would POST only our two properties and silently wipe the
  // siblings from the order (e.g. ship un-sewn webbing). The whole CART
  // comes back with them: the stock gate needs the other lines' quantities,
  // and one read serves both.
  async function readCartLine(line) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const item = cart?.items?.[line - 1];
      if (!item) return null;
      return { cart, properties: item.properties ? { ...item.properties } : {} };
    } catch (_e) {
      return null;
    }
  }

  async function submitCartChange({ line, pieces, length, unit, lengthProp, piecesProp, row, sourceEl }) {
    const newQuantity = cartLineQuantity(length, pieces);
    const lengthValue = unit ? `${length} ${unit}` : String(length);
    // Clear any previous refusal as soon as the customer commits a new
    // value, so a stale message never sits over an edit that's in flight.
    setLineError(row, '');
    const current = await readCartLine(line);
    if (current === null) {
      // Can't see the line's current properties — abort rather than wipe
      // them (see readCartLine). Returning null makes the editor restore
      // its previous displayed value.
      console.error('[bc-cart-length] aborting change: could not read current line properties');
      return null;
    }

    // Refuse an overselling edit before Shopify can silently clamp it.
    const refusal = stockRefusal({ row, cart: current.cart, line, quantity: newQuantity, unit });
    if (refusal) {
      setLineError(row, refusal);
      return null;
    }
    const existingProperties = current.properties;

    const properties = {
      ...existingProperties,
      [lengthProp || 'Piece Length']: lengthValue,
      [piecesProp || 'Qty']: String(pieces),
    };
    // Segment Builder lines mirror their spec in the Build permalink
    // property, and the cart's "Edit segment" link restores qty/length
    // from ITS q=/len= params (bc-segment-builder.js _applyUrl) — keep
    // them in step with this edit or the builder reopens on stale values.
    // Same q-only-above-1 rule as the builder's _buildParams/_mergeAdd.
    //
    // WHICH length param, though: a Segment Builder build is up to three
    // cart lines (mainline / backup / extender) that all carry the SAME
    // whole-build permalink, so the line's own "Segment Piece" property is
    // what says which of blen=/xlen= this edit belongs in. Rewriting the
    // unprefixed len= from a backup line — as this did before the backup
    // landed — wrote the backup's metres into the MAINLINE's slot, leaving
    // the build's three permalinks disagreeing with each other and with
    // the line's own Piece Length. A line with no role property is a solo
    // mainline (every line written before build ids existed), which is why
    // the empty prefix is the default rather than a special case.
    //
    // `q` stays UNPREFIXED on every line: there is one system quantity per
    // build, not one per line. Writing it from whichever line was edited
    // is right — it's the customer's latest statement of how many systems
    // they want — even though the OTHER lines' Qty properties now disagree
    // with it. The builder surfaces that on reopen rather than silently
    // reconciling it (bc-segment-builder.js _noteQtyDesync).
    if (typeof properties.Build === 'string' && properties.Build.includes('&e1=')) {
      try {
        const url = new URL(properties.Build);
        const role = String(properties['Segment Piece'] || '').trim().toLowerCase();
        const prefix = role === 'backup' ? 'b' : (role === 'extender' ? 'x' : '');
        if (pieces > 1) url.searchParams.set('q', String(pieces));
        else url.searchParams.delete('q');
        if (url.searchParams.get(`${prefix}mode`) === 'custom') {
          url.searchParams.set(`${prefix}len`, String(length));
        }
        properties.Build = url.toString();
      } catch (_e) { /* malformed permalink — pass it through untouched */ }
    }

    const body = {
      line,
      quantity: newQuantity,
      properties,
      sections: window.bcCartAdd.collectSectionIds(),
      sections_url: window.location.pathname,
    };

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.errors) {
        console.error('[bc-cart-length] /cart/change error:', data.errors);
        return null;
      }
      const dispatcher = sourceEl || document;
      dispatcher.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'bc-cart-length',
            data: {
              sections: data.sections,
              itemCount: data.item_count,
              source: 'bc-cart-length',
            },
          },
        })
      );
      return data;
    } catch (err) {
      console.error('[bc-cart-length] /cart/change fetch failed:', err);
      return null;
    }
  }

  /*
   * <bc-cart-length-editor>
   *
   * Inline editor for the length-per-piece property. Reads the row's current
   * piece count from `data-bc-pieces`, submits the new length × current pieces.
   */
  if (!customElements.get('bc-cart-length-editor')) {
    class BcCartLengthEditor extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input.bc-cart-length-editor__input');
        if (!this.input) return;
        this._original = this.input.value;
        this._submitting = false;

        this._onBlur = () => this._maybeSubmit();
        this._onKeyDown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.input.blur();
          } else if (e.key === 'Escape') {
            this.input.value = this._original;
            this.input.blur();
          }
        };

        this.input.addEventListener('blur', this._onBlur);
        this.input.addEventListener('keydown', this._onKeyDown);
      }

      disconnectedCallback() {
        this.input?.removeEventListener('blur', this._onBlur);
        this.input?.removeEventListener('keydown', this._onKeyDown);
      }

      async _maybeSubmit() {
        if (this._submitting) return;
        const newLength = parseFloat(this.input.value);
        if (!Number.isFinite(newLength) || newLength <= 0) {
          this.input.value = this._original;
          return;
        }
        if (parseFloat(this._original) === newLength) return;

        const row = findRow(this);
        const state = readRowState(row);
        const line = readLineIndex(this);
        if (!row || !state || line == null) {
          this.input.value = this._original;
          return;
        }

        this._submitting = true;
        try {
          const result = await submitCartChange({
            line,
            pieces: state.pieces,
            length: newLength,
            unit: this.dataset.unit || 'm',
            ...readPropNames(row),
            row,
            sourceEl: this,
          });
          if (result) this._original = String(newLength);
          else this.input.value = this._original;
        } finally {
          this._submitting = false;
        }
      }
    }
    customElements.define('bc-cart-length-editor', BcCartLengthEditor);
  }

  /*
   * <bc-cart-pieces-editor>
   *
   * Inline editor for the pieces property. Reads the row's current
   * length-per-piece from `data-bc-length-per-piece`, submits new
   * pieces × current length.
   */
  if (!customElements.get('bc-cart-pieces-editor')) {
    class BcCartPiecesEditor extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input.bc-cart-pieces-editor__input');
        if (!this.input) return;
        this._original = this.input.value;
        this._submitting = false;

        this._onBlur = () => this._maybeSubmit();
        this._onKeyDown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.input.blur();
          } else if (e.key === 'Escape') {
            this.input.value = this._original;
            this.input.blur();
          }
        };

        this.input.addEventListener('blur', this._onBlur);
        this.input.addEventListener('keydown', this._onKeyDown);
      }

      disconnectedCallback() {
        this.input?.removeEventListener('blur', this._onBlur);
        this.input?.removeEventListener('keydown', this._onKeyDown);
      }

      async _maybeSubmit() {
        if (this._submitting) return;
        const newPieces = parseInt(this.input.value, 10);
        if (!Number.isFinite(newPieces) || newPieces <= 0) {
          this.input.value = this._original;
          return;
        }
        if (parseInt(this._original, 10) === newPieces) return;

        const row = findRow(this);
        const state = readRowState(row);
        const line = readLineIndex(this);
        if (!row || !state || line == null) {
          this.input.value = this._original;
          return;
        }

        this._submitting = true;
        try {
          const result = await submitCartChange({
            line,
            pieces: newPieces,
            length: state.length,
            unit: readUnit(row),
            ...readPropNames(row),
            row,
            sourceEl: this,
          });
          if (result) this._original = String(newPieces);
          else this.input.value = this._original;
        } finally {
          this._submitting = false;
        }
      }
    }
    customElements.define('bc-cart-pieces-editor', BcCartPiecesEditor);
  }
})();
